import { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface NavbarProps {
  /** Logo slot - typically an image or text logo */
  logo: ReactNode;
  /** Navigation links slot - typically a list of links */
  links?: ReactNode;
  /** Actions slot - typically CTA buttons */
  actions?: ReactNode;
  className?: string;
}

/**
 * Navbar component with slot-based composition.
 *
 * Desktop: [Logo (flex-1)] --- [Links (centered)] --- [Actions (flex-1, justify-end)]
 * Mobile: [Logo (flex-1)] --- [Actions + Menu (justify-end)]
 *
 * @example
 * ```tsx
 * <Navbar
 *   logo={<Logo />}
 *   links={<NavLinks />}
 *   actions={<Button>Get Started</Button>}
 *   mobileMenu={<MobileMenuButton />}
 * />
 * ```
 */
export function Navbar({
  logo,
  links,
  actions,
  className,
}: NavbarProps) {
  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full",
        "h-navbar-mobile md:h-navbar",
        "flex flex-col justify-center items-center",
        "pl-px-mobile pr-3 md:px-px-tablet lg:px-px-desktop",
        "bg-background",
        className
      )}
    >
      {/* Container - 3 sections with gap, links centered on desktop */}
      <nav className="flex w-full items-center justify-center gap-4 md:gap-8">
        {/* Logo - left side, grows to push links to center */}
        <div className="flex items-center flex-1">
          {logo}
        </div>

        {/* Links - center, does not grow, hidden on mobile */}
        {links && (
          <div className="hidden md:flex items-center">
            {links}
          </div>
        )}

        {/* Actions - right side, grows on desktop, content pushed to end */}
        <div className="flex items-center justify-end md:flex-1 gap-2 md:gap-4">
          {actions}
        </div>
      </nav>
    </header>
  );
}
